import {
    Box,
    ChevronLeftIcon,
    ChevronRightIcon,
    CloseIcon,
    HStack,
    Image,
    ModalBackdrop,
    ModalBodyUI,
    ModalCloseButton,
    ModalContent,
    ModalHeaderUI,
    ModalUI,
    Pressable,
    Tooltip,
    usePrevious,
    UserContext,
    VStack
} from "@hosttools/frontend";
import type React from "react";
import {memo, useCallback, useContext, useEffect, useState} from "react";
import {useLocation} from "react-router-dom";

import QuickGuideButton from "../../features/QuickGuide/QuickGuideButton";
import MenuItem from "../MenuItem";
import MessasingMenu from "../NavMessaging/MessagingMenu";
import Text from "../Text";

import AvatarProfile from "./AvatarProfile";
import MenuProfile from "./MenuProfile";
import PricingMenu from "./PricingMenu";

import {INBOX_URL, MULTICALENDAR_PATH} from "@/admin/constant";
import logoBlack from "@/admin/img/logo-black.svg";

type Props = {
    isOpen: boolean;
    subscriptionStatus: string;
    onHide: () => void;
};

type MenuDisplay = "messaging" | "pricing" | "main" | "profile";

const logoSource = {uri: logoBlack};

const ModalMenuMobile: React.FC<Props> = ({isOpen, subscriptionStatus, onHide}) => {
    const {listingGroups} = useContext(UserContext);
    const location = useLocation();
    const prevPathName = usePrevious(location.pathname);
    const [menuDisplay, setMenuDisplay] = useState<MenuDisplay>("main");

    const handleClickPricing = useCallback(() => {
        setMenuDisplay("pricing");
    }, []);

    const handleClickMessaging = useCallback(() => {
        setMenuDisplay("messaging");
    }, []);

    const handleClickProfile = useCallback(() => {
        setMenuDisplay("profile");
    }, []);

    const handleBack = useCallback(() => {
        setMenuDisplay("main");
    }, []);

    useEffect(() => {
        if (prevPathName !== location.pathname) {
            onHide();
        }
    }, [location.pathname, onHide, prevPathName]);

    return (
        <ModalUI isOpen={isOpen} size="xl" onClose={onHide}>
            <ModalBackdrop />
            <ModalContent
                className="mb-auto mt-10"
                // for web only
                // @ts-expect-error
                style={{
                    maxHeight: "calc(var(--vh, 1vh) * 100 - 50px)"
                }}
            >
                <ModalHeaderUI testID="modal-header">
                    {menuDisplay === "main" && (
                        <Image
                            alt="Host Tools Logo"
                            className="h-[22px] w-[110px]"
                            source={logoSource}
                        />
                    )}
                    {menuDisplay !== "main" && (
                        <Pressable className="flex-row items-center gap-2" onPress={handleBack}>
                            <ChevronLeftIcon color="blueGray.600" />
                            <Text variant="md">
                                {menuDisplay === "messaging" && "Messaging"}
                                {menuDisplay === "pricing" && "Pricing"}
                            </Text>
                            {menuDisplay === "profile" && <AvatarProfile />}
                        </Pressable>
                    )}
                    <Tooltip label="Close">
                        <ModalCloseButton>
                            <CloseIcon color="blueGray.400" />
                        </ModalCloseButton>
                    </Tooltip>
                </ModalHeaderUI>
                <Box className="my-1 border-t border-dashed border-blueGray-300" />
                <ModalBodyUI className="px-0" testID="modal-body">
                    {menuDisplay === "main" && (
                        <VStack className="px-5 pt-4" space="lg">
                            {subscriptionStatus !== "active" &&
                                subscriptionStatus !== "canceled" && <QuickGuideButton />}
                            <MenuItem path={MULTICALENDAR_PATH} onClick={onHide}>
                                Calendar
                            </MenuItem>
                            <MenuItem path={INBOX_URL} onClick={onHide}>
                                Inbox
                            </MenuItem>
                            <MenuItem
                                icon={<ChevronRightIcon color="blueGray.400" />}
                                onClick={handleClickMessaging}
                            >
                                Messaging
                            </MenuItem>
                            <MenuItem
                                icon={<ChevronRightIcon color="blueGray.400" />}
                                onClick={handleClickPricing}
                            >
                                Pricing
                            </MenuItem>
                            <Pressable
                                className="flex-row items-center justify-between bg-blueGray-100 rounded-md p-2"
                                onPress={handleClickProfile}
                            >
                                <HStack className="items-center" space="sm">
                                    <AvatarProfile showName />
                                </HStack>
                                <ChevronRightIcon color="blueGray.400" />
                            </Pressable>
                        </VStack>
                    )}
                    {menuDisplay === "pricing" && <PricingMenu />}
                    {menuDisplay === "messaging" && <MessasingMenu listingGroups={listingGroups} />}
                    {menuDisplay === "profile" && <MenuProfile showMenuOnly />}
                </ModalBodyUI>
            </ModalContent>
        </ModalUI>
    );
};

export default memo(ModalMenuMobile);
